import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";

import { isEqualWith, isNull } from "lodash";
import { showError, showSuccess } from "redux/actions/ui";
import Popup from "components/popup/Popup";
import { IUserPermissions } from "redux/reducers/models/user";

import { useTranslation } from "react-i18next";
import {
  GetFacilityAdmin,
  PostPasswordResetCode,
  PutDisableFacilityAdmin,
  PutFacilityAdmin,
  PutReactivateFacilityAdmin,
  PutFacilityAdminPermissions,
  GetFacilityPermissions,
  GetFacilityRoles,
  IFacilityRole,
  IPermissionDetails,
  IPermissionDetailsList,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { StaffCard } from "components/staffCard/StaffCard";
import Tabs from "components/tabs/Tabs";
import Callout from "components/callout/Callout";
import StaffAccountShiftTable from "../shifts/staffAccountShiftTable/StaffAccountShiftTable";
import PermissionEditor from "./permissions/PermissionEditor";
import { permissionsAfterRoleChange } from "./permissions/rolePermissionMerge";
import Card from "components/card/Card";
import { ButtonNew } from "components/buttonNew";
import DataTable from "components/dataTable/DataTable";
import Input from "components/form/input/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import { LocaleCurrency } from "helpers/Locale";

export interface IStaff {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  state: string;
  quick_code: string;
  has_quick_code?: boolean;
  password?: string;
  permissions: IUserPermissions;
  profile_image_source: string;
  phone_number: string;
  role_id: number;
  role_title: string;
  reportPermissions: IUserPermissions & { [key: string]: boolean };
  roles?: Array<{ id: number; title: string; wage?: string }>;
}

interface ILocationState {
  params: { selectedTab: number };
}

export default function FacilityStaffDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { authStore, facilityStore } = useAppSelector(store => store);

  const authedUser = authStore?.user?.permissions;

  const [staffState, setStaffState] = useState<IStaff & { roles?: Array<{ id: number; title: string; wage?: string }> }>(undefined);
  const [staffStateBeforeChanges, setStaffStateBeforeChanges] = useState<IStaff>(undefined);
  const [roleCatalog, setRoleCatalog] = useState<IFacilityRole[]>(null);

  const [resetPasswordPopup, setResetPasswordPopup] = useState<boolean>(false);
  const [disableAccountPopup, setDisableAccountPopup] = useState<boolean>(false);

  const [reactivateAccountPopup, setReactivateAccountPopup] = useState<boolean>(false);
  const [permissionState, setPermissionState] = useState<IPermissionDetailsList>(null);

  const {
    state: starterTeeSheetPopup,
    updateModal: updateStarterTeeSheetPopup,
    closeModal: closeStarterTeeSheetPopup,
  } = useModal();

  const { state: kioskViewPopup, updateModal: updateKioskViewPopup, closeModal: closeKioskViewPopup } = useModal();

  const { state: roleModal, updateModal: updateRoleModal, closeModal: closeRoleModal } = useModal<{ selectedRoles: Array<{ id: number; title: string; wage?: string }> }>({ selectedRoles: [] });

  const tabs = [
    {
      id: "permissions",
      content: t("Permissions"),
    },
    {
      id: "roles",
      content: t("Roles"),
    },
    {
      id: "shift-history",
      content: t("Shift History"),
    },
  ];
  const [selected, setSelected] = useState(0);
  const staffHasPin = Boolean(staffState?.has_quick_code);

  useEffect(() => {
    void loadFacilityStaff();
    void loadPermissionDetails();
    void loadFacilityRoles();
    void setDefaultTab();
  }, []);

  useEffect(() => {
    if (!permissionState) {
      return;
    }

    // Sort permissions
    const sortedKeys = Object.keys(permissionState).sort((a, b) => a.localeCompare(b));

    const sortedPermissions = sortedKeys.reduce((acc, key) => {
      acc[key] = permissionState[key];
      return acc;
    }, {} as typeof permissionState);

    // Only update if changed
    const isSame = JSON.stringify(sortedPermissions) === JSON.stringify(permissionState);

    if (!isSame) {
      setPermissionState(sortedPermissions);
    }
  }, [permissionState]);

  function setDefaultTab() {
    const locationState = location.state as ILocationState;
    if (locationState?.params) {
      setSelected(locationState?.params?.selectedTab);
    }
  }

  async function loadFacilityStaff(resetBaseline = false) {
    const params = {
      id: id,
      extended: true,
      roles: true,
    };
    const staffRes = await GetFacilityAdmin(params, true);
    if (staffRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error when loading staff member")));
      return;
    }

    const permissions: IUserPermissions & { [key: string]: any } = { ...staffRes.data[0]?.permissions };

    const nextStaff = {
      id: staffRes.data[0].id,
      first_name: staffRes.data[0].first_name ?? "",
      last_name: staffRes.data[0].last_name ?? "",
      email: staffRes.data[0].email ?? "",
      username: staffRes.data[0].username ?? "",
      state: staffRes.data[0].state ?? "",
      quick_code: "",
      has_quick_code: Boolean(staffRes.data[0]?.has_quick_code),
      profile_image_source: staffRes.data[0].profile_image_source,
      phone_number: staffRes.data[0].phone_number,
      role_id: staffRes.data[0].role_id,
      permissions: permissions,
      roles: staffRes?.data[0]?.roles ?? [],
    };

    setStaffState(prev => ({
      ...prev,
      ...nextStaff,
    }));

    if (resetBaseline) {
      setStaffStateBeforeChanges(prev => ({
        ...prev,
        ...nextStaff,
      }));
    }
  }

  async function loadPermissionDetails() {
    const permissionDetails = await GetFacilityPermissions({}, true);

    if (permissionDetails.status !== StatusCode.OK) {
      dispatch(showError("Error retrieving permissions"));
      return;
    }
    setPermissionState(permissionDetails.data);
  }

  async function loadFacilityRoles() {
    const roleRes = await GetFacilityRoles({ permissions: true }, false);
    if (roleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility roles")));
      return;
    }

    setRoleCatalog(roleRes.data);
  }

  function roleIds(roles?: Array<{ id: number }>) {
    return (roles ?? []).map(role => role.id).sort((a, b) => a - b);
  }

  function checkStarterTeeSheet() {
    if (
      staffState?.permissions?.tee_sheet_starter &&
      staffState?.permissions?.tee_sheet_starter !== staffStateBeforeChanges?.permissions?.tee_sheet_starter
    ) {
      void updateStarterTeeSheetPopup({ isOpen: true });
    } else if (
      staffState?.permissions?.kiosk_view &&
      staffState?.permissions?.kiosk_view !== staffStateBeforeChanges?.permissions?.kiosk_view
    ) {
      void updateKioskViewPopup({ isOpen: true });
    } else {
      void saveStaffDetails();
    }
  }

  async function saveStaffDetails() {
    const currentRoles = (staffState.roles ?? []).map(role => ({
      id: role.id,
      wage: role.wage === "" || role.wage == null ? undefined : role.wage,
    }));
    const previousRoles = (staffStateBeforeChanges?.roles ?? []).map(role => ({
      id: role.id,
      wage: role.wage === "" || role.wage == null ? undefined : role.wage,
    }));
    const rolesChanged = JSON.stringify(currentRoles) !== JSON.stringify(previousRoles);
    const roleIdsChanged = JSON.stringify(roleIds(staffState.roles)) !== JSON.stringify(roleIds(staffStateBeforeChanges?.roles));
    const putFacilityAdminResponse = await PutFacilityAdmin(
      {
        id: staffState.id,
        first_name: staffState.first_name,
        last_name: staffState.last_name,
        email: staffState.email,
        username: staffState.username,
        ...(rolesChanged ? { roles: currentRoles } : {}),
      },
      true,
    );

    if (putFacilityAdminResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error when updating staff member")));
      return;
    }

    // Role updates rewrite permissions from the roles still assigned.
    // Write the edited flags after that so a permission turned on in this same save is kept,
    // including one enabled after the role was removed.
    if (permissionsChanged() || roleIdsChanged) {
      const putFacilityAdminPermissionsResponse = await PutFacilityAdminPermissions(
        {
          user_id: parseInt(id),
          ...staffState.permissions,
        },
        true,
      );

      if (putFacilityAdminPermissionsResponse.status !== StatusCode.OK) {
        dispatch(showError(t("Error when updating staff member permissions")));
        return;
      }
    }

    dispatch(showSuccess(t("Staff account saved successfully")));

    if (starterTeeSheetPopup?.isOpen) {
      void closeStarterTeeSheetPopup();
    }

    if (kioskViewPopup?.isOpen) {
      void closeKioskViewPopup();
    }

    if (roleIdsChanged) {
      await loadFacilityStaff(true);
      return;
    }

    setStaffStateBeforeChanges(staffState);
  }

  function setPermissionChecked(permissionKey: string, checked: boolean) {
    setStaffState(prev => {
      return { ...prev, permissions: { ...prev.permissions, [permissionKey]: checked } };
    });
  }

  function applyPermissionToggles(permissions: IPermissionDetails[], checked: boolean) {
    const permissionsToToggle: { [key: string]: boolean } = {};

    permissions.forEach(permission => {
      permissionsToToggle[permission.key] = checked;
    });

    setStaffState(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        ...permissionsToToggle,
      },
    }));
  }

  async function handlePasswordReset() {
    const resetRes = await PostPasswordResetCode(
      { email: staffStateBeforeChanges.email, facility_id: authStore?.user?.facility_id ?? undefined },
      true,
    );
    if (resetRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending email")));
      return;
    }

    setResetPasswordPopup(false);
    dispatch(showSuccess(t("Email sent successfully")));
  }

  async function handleReactivateAccount() {
    const activateRes = await PutReactivateFacilityAdmin({ admin_id: staffState.id }, true);

    if (activateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Disabled")));
    }

    history.push("/admin/settings/staff");
  }

  async function handleDisableStaffAccount() {
    const putDisableFacilityAdminResponse = await PutDisableFacilityAdmin({ admin_id: staffState.id }, true);

    if (putDisableFacilityAdminResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to disable user")));
      return;
    }

    history.push("/admin/settings/staff");
  }

  function permissionsMatch(left?: IUserPermissions, right?: IUserPermissions) {
    return isEqualWith(left, right, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function permissionsChanged() {
    return !permissionsMatch(staffStateBeforeChanges?.permissions, staffState?.permissions);
  }

  // Permissions a role add/remove would apply on top of the last saved flags.
  // Manual toggles are the only difference that should show the local-permissions callout.
  function permissionsFromCurrentRoles() {
    const savedPermissions = staffStateBeforeChanges?.permissions;
    if (!savedPermissions) {
      return staffState?.permissions;
    }

    const savedRoles = staffStateBeforeChanges?.roles ?? [];
    const currentRoles = staffState?.roles ?? [];
    if (JSON.stringify(roleIds(currentRoles)) === JSON.stringify(roleIds(savedRoles))) {
      return savedPermissions;
    }

    return permissionsAfterRoleChange(roleCatalog, savedRoles, currentRoles, savedPermissions);
  }

  function localPermissionsChanged() {
    return !permissionsMatch(permissionsFromCurrentRoles(), staffState?.permissions);
  }

  function resetPermissions() {
    setStaffState(prev => ({
      ...prev,
      permissions: { ...permissionsFromCurrentRoles() },
    }));
  }

  // function handleSavePermissionChecks() {
  //   if (!staffStateBeforeChanges?.permissions?.tee_sheet_starter && staffState?.permissions?.tee_sheet_starter) {
  //     void updateStarterTeeSheetPopup({ isOpen: true });
  //   } else {
  //     void saveStaffDetails();
  //   }
  // }

  function assignRoles(nextRoles: Array<{ id: number; title: string; wage?: string }>) {
    setStaffState(prev => {
      const idsChanged = JSON.stringify(roleIds(nextRoles)) !== JSON.stringify(roleIds(prev?.roles));
      return {
        ...prev,
        roles: nextRoles,
        permissions: idsChanged
          ? permissionsAfterRoleChange(roleCatalog, prev.roles, nextRoles, prev.permissions)
          : prev.permissions,
      };
    });
  }

  function handleSelectNewRole(role: Partial<IFacilityRole>) {
    let updatedRoles = roleModal?.selectedRoles ? [...roleModal.selectedRoles] : [];
    const foundRole = updatedRoles.find(updatedRole => updatedRole.id === role.id);
    if (foundRole) {
      updatedRoles = updatedRoles.filter(updatedRole => updatedRole.id !== role.id);
    } else {
      updatedRoles.push({ id: role.id, title: role.title, wage: role.wage });
    }
    updateRoleModal({ selectedRoles: updatedRoles });
  }

  function handleRemoveRole(role: { id: number }) {
    assignRoles((staffState?.roles ?? []).filter(assignedRole => assignedRole.id !== role.id));
  }

  function handleAddNewRoles() {
    const updatedRoles = [...(staffState?.roles ?? []), ...(roleModal?.selectedRoles ?? [])];
    assignRoles(updatedRoles);
    void closeRoleModal();
  }

  function handleWageChange(event: React.ChangeEvent<HTMLInputElement>, index: number) {
    const updatedRoles = staffState?.roles ? [...staffState.roles] : [];
    updatedRoles[index] = { ...updatedRoles[index], wage: event.target.value };
    assignRoles(updatedRoles);
  }

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  function checkStatus(permission: IPermissionDetails) {
    return staffState?.permissions?.[permission.key as keyof IUserPermissions] as boolean;
  }

  function unsavedChangesExist() {
    if (staffStateBeforeChanges === undefined) {
      if (staffState) {
        setStaffStateBeforeChanges(staffState);
      }
      return false;
    }

    return !isEqualWith(staffStateBeforeChanges, staffState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    if (starterTeeSheetPopup?.isOpen) {
      void closeStarterTeeSheetPopup();
    }
    setStaffState(staffStateBeforeChanges);
  }

  const isOwnAccount = Number(authStore.user?.id) === Number(staffState?.id);
  const permissionsLocked =
    staffState?.state === "disabled" || !authedUser?.admin_accounts_edit || isOwnAccount;
  const canEditRoles = staffState?.state !== "disabled" && Boolean(authedUser?.admin_accounts_edit) && !isOwnAccount;

  return (
    <Page
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Staff Accounts"), url: "/admin/settings/staff" },
        {
          label: staffState ? `${staffState.first_name} ${staffState.last_name}` : t("Staff account"),
          url: `/admin/settings/staff/${id}`,
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: checkStarterTeeSheet,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <StaffCard
        staff={staffState}
        staffRoles={staffState?.roles}
        onResetPassword={() => setResetPasswordPopup(true)}
        onDisableAccount={() => setDisableAccountPopup(true)}
        onReactivateAccount={() => setReactivateAccountPopup(true)}
      />
      <Page.Section>
        {staffState?.state !== "disabled" ? (
          <div style={{ display: "flex", overflowX: "auto", paddingBottom: 24, paddingTop: 24 }}>
            <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} variant="wide" />
          </div>
        ) : null}
      </Page.Section>

      {staffState?.state === "disabled" ? (
        <div className="mt-4 mb-4">
          <Callout
            type="alerts"
            title={t("This account has been disabled.")}
            content={t("This staff member no longer has access to their account.")}
          />
        </div>
      ) : null}
      {selected === 0 ? (
        <div className="mb-10">
          {staffState && (
            <>
              {/* // TODO: Turn into component */}
              <div>
                {localPermissionsChanged() && (
                  <div className="mb-4">
                    <Callout
                      type="info"
                      title={t("You are changing local permissions for a staff member")}
                      content={t("These changes will not affect the global permissions for their assigned role.")}
                      onClose={() => resetPermissions()}
                    />
                  </div>
                )}
                <PermissionEditor
                  permissions={permissionState}
                  title={t("Permissions")}
                  disabled={permissionsLocked}
                  isChecked={permission => Boolean(checkStatus(permission))}
                  onToggle={(permission, checked) => setPermissionChecked(permission.key, checked)}
                  onToggleMany={applyPermissionToggles}
                  isRowDisabled={permission =>
                    permission.key === "kiosk_view" && !staffHasPin && !checkStatus(permission)
                  }
                  rowDescription={permission =>
                    permission.key === "kiosk_view" && !staffHasPin && !checkStatus(permission)
                      ? t("A Quick Code is required before enabling Kiosk View.")
                      : permission.description
                  }
                />
              </div>
            </>
          )}
        </div>
      ) : null}

      {selected === 1 ? (
        <Card
          title={t("Roles")}
          titleActions={
            canEditRoles
              ? [
                  {
                    content: t("Add roles"),
                    buttonType: "primary",
                    action: () => updateRoleModal({ isOpen: true, selectedRoles: [] }),
                  },
                ]
              : undefined
          }
        >
          <Card.Section>
            <DataTable
              columns={[
                { label: t("Role"), width: "50%" },
                ...(authedUser?.admin_view_shift_wages ? [{ label: t("Wage"), width: "40%" }] : []),
                { label: "", width: "10%" },
              ]}
            >
              {!staffState?.roles || staffState.roles.length === 0 ? (
                <td colSpan={authedUser?.admin_view_shift_wages ? 3 : 2}>
                  <MessageDisplay
                    title={t("No roles set")}
                    content={t("There are no roles set on this account.")}
                    icon="magnifying-glass"
                  />
                  {canEditRoles ? (
                    <div className="flex justify-center pb-4">
                      <ButtonNew type="primary" onClick={() => updateRoleModal({ isOpen: true, selectedRoles: [] })}>
                        {t("Add roles")}
                      </ButtonNew>
                    </div>
                  ) : null}
                </td>
              ) : (
                staffState.roles.map((role, index) => (
                  <tr key={role.id}>
                    <td>{role.title}</td>
                    {authedUser?.admin_view_shift_wages ? (
                      <td>
                        <Input
                          onChange={event => handleWageChange(event, index)}
                          labelHidden
                          value={role.wage ?? ""}
                          prefix={facilityStore?.facility?.default_currency_symbol}
                          suffix={t("/hr")}
                          placeholder="0.00"
                          disabled={!canEditRoles}
                        />
                      </td>
                    ) : null}
                    <td>
                      {canEditRoles ? (
                        <FontAwesomeIcon icon={faXmark} className="cursor-pointer" onClick={() => handleRemoveRole(role)} />
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </DataTable>
          </Card.Section>
        </Card>
      ) : null}

      {selected === 2 ? (
        <>
          <StaffAccountShiftTable userId={Number(id)} addNotes={authStore?.user?.id === Number(id)} />
        </>
      ) : null}

      <Popup
        open={resetPasswordPopup}
        type="info"
        title={t("Reset Account Password?")}
        description={t("An email will be sent to reset your account password")}
        onOk={handlePasswordReset}
        okText={t("Send")}
        cancelText={t("Cancel")}
        onCancel={() => setResetPasswordPopup(false)}
      />

      <Popup
        open={reactivateAccountPopup}
        type="info"
        title={t("Reactivate Staff Account?")}
        description={t("Are you sure you want to reactivate this staff account?")}
        onOk={handleReactivateAccount}
        okText={t("Activate")}
        cancelText={t("Cancel")}
        onCancel={() => setReactivateAccountPopup(false)}
      />

      <Popup
        open={disableAccountPopup}
        type="info"
        title={t("Diable Staff Account?")}
        description={t("Are you sure you want to disable this staff account?")}
        onOk={handleDisableStaffAccount}
        okText={t("Disable")}
        cancelText={t("Cancel")}
        onCancel={() => setDisableAccountPopup(false)}
      />
      {/* 
      <Popup
        open={deletePopup}
        type="warning"
        title={t("Delete Staff Account?")}
        description={t("Are you sure you want to delete this staff account? This is a permanent action.")}
        onOk={handleDeleteStaffAccount}
        okText={t("Delete")}
        cancelText={t("Cancel")}
        onCancel={() => setDeleteAccountPopup(false)}
      /> */}

      <Popup
        open={starterTeeSheetPopup?.isOpen}
        type="info"
        title={t("Starter Tee Sheet Permission Set")}
        description={t("This user will only have access to the starter tee sheet. Are you sure you want to continue?")}
        onOk={saveStaffDetails}
        okText={t("Continue")}
        cancelText={t("Cancel")}
        onCancel={closeStarterTeeSheetPopup}
      />

      <Popup
        open={kioskViewPopup?.isOpen}
        type="info"
        title={t("Kiosk View Permission Set")}
        description={t("This user will only have access to the kiosk view. Are you sure you want to continue?")}
        onOk={saveStaffDetails}
        okText={t("Continue")}
        cancelText={t("Cancel")}
        onCancel={closeKioskViewPopup}
      />
      <Portal isMounted={roleModal?.isOpen}>
        <Sheet
          open={roleModal?.isOpen}
          title={t("Add roles")}
          size="small"
          onCancel={closeRoleModal}
          onOk={handleAddNewRoles}
          okText={t("Add")}
          okDisabled={roleModal?.selectedRoles?.length === 0}
        >
          <div className="ui-checkbox-group">
            {roleCatalog?.filter(role => !staffState?.roles?.some(staffRole => staffRole.id === role.id))?.length === 0 ? (
              <MessageDisplay
                title={t("No roles available")}
                content={t("Every facility role is already assigned to this account.")}
                icon="magnifying-glass"
              />
            ) : null}
            {roleCatalog
              ?.filter(role => !staffState?.roles?.some(staffRole => staffRole.id === role.id))
              ?.map(role => {
                const checked = roleModal?.selectedRoles?.some(selectedRole => selectedRole.id === role.id);
                return (
                  <div
                    key={role.id}
                    onClick={() => handleSelectNewRole(role)}
                    className={classNames("ui-checkbox-group-item", {
                      "ui-checkbox-group-item_selected": checked,
                    })}
                  >
                    <div className="ui-checkbox-group-item-content">
                      <div className="ui-checkbox-group-item-text">
                        <p className="text-semibold">{role.title}</p>
                        {role.wage && authedUser?.admin_view_shift_wages ? (
                          <p>
                            <LocaleCurrency amount={role.wage} currency={facilityStore?.facility?.default_currency_code} />
                            {t("/hr")}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <Checkbox size="medium" checked={checked} onChange={() => handleSelectNewRole(role)} />
                  </div>
                );
              })}
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
